import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { TranslateLoader, TranslationObject } from '@ngx-translate/core';
import { Observable, firstValueFrom, from } from 'rxjs';
import {
  DEFAULT_PROGRESSIVE_RETRY_DELAYS_MS,
  runWithProgressiveRetry,
} from '@ts-helpers/progressive-retry';

const DEFAULT_LANG = 'en';
const I18N_ASSET_PREFIX = '/assets/i18n';

export interface TranslationLoaderNetwork {
  waitUntilOnline: () => Observable<true>;
  confirmOfflineAfterFailure: () => Promise<boolean>;
}

export interface TranslationRetryExhaustedEvent {
  lang: string;
  error: unknown;
}

export interface ResilientTranslateLoaderOptions {
  retryDelaysMs?: readonly number[];
  wait?: (delayMs: number) => Promise<void>;
  onLoadStart?: () => void;
  onLoadEnd?: () => void;
  onRetryExhausted?: (event: TranslationRetryExhaustedEvent) => void;
  network?: TranslationLoaderNetwork;
}

export class ResilientTranslateLoader extends TranslateLoader {
  private retryExhaustionReported = false;

  constructor(
    private readonly http: HttpClient,
    private readonly options: ResilientTranslateLoaderOptions = {}
  ) {
    super();
  }

  getTranslation(lang: string): Observable<TranslationObject> {
    return from(this.loadWithLoadingIndicator(normalizeLang(lang)));
  }

  private async loadWithLoadingIndicator(lang: string): Promise<TranslationObject> {
    this.options.onLoadStart?.();

    try {
      return await this.loadTranslation(lang);
    } finally {
      this.options.onLoadEnd?.();
    }
  }

  private async loadTranslation(lang: string): Promise<TranslationObject> {
    if (lang === DEFAULT_LANG) {
      return this.loadDefaultTranslation();
    }

    try {
      return await this.loadWithRetry(lang);
    } catch (error) {
      if (error instanceof TranslationRetryExhaustedError) {
        throw error;
      }

      console.warn(`Failed to load ${lang} translations. Falling back to ${DEFAULT_LANG}.`, error);

      return this.loadDefaultTranslation();
    }
  }

  private async loadDefaultTranslation(): Promise<TranslationObject> {
    try {
      return await this.loadWithRetry(DEFAULT_LANG);
    } catch (error) {
      console.error(`Failed to load ${DEFAULT_LANG} translations.`, error);
      return {};
    }
  }

  private async loadWithRetry(lang: string): Promise<TranslationObject> {
    let retryScheduled = false;

    try {
      return await runWithProgressiveRetry(() => this.requestTranslation(lang), {
        delaysMs: this.options.retryDelaysMs ?? DEFAULT_PROGRESSIVE_RETRY_DELAYS_MS,
        shouldRetry: isRetryableTranslationError,
        wait: this.options.wait,
        onRetry: () => {
          retryScheduled = true;
        },
      });
    } catch (error) {
      if (retryScheduled || isRetryableTranslationError(error)) {
        this.reportRetryExhaustion(lang, error);
        throw new TranslationRetryExhaustedError(error);
      }

      throw error;
    }
  }

  private async requestTranslation(lang: string): Promise<TranslationObject> {
    const network = this.options.network;

    for (;;) {
      if (network) {
        await firstValueFrom(network.waitUntilOnline());
      }

      try {
        return await firstValueFrom(
          this.http.get<TranslationObject>(`${I18N_ASSET_PREFIX}/${lang}.json`)
        );
      } catch (error) {
        if (!network || !isOfflineTranslationError(error)) {
          throw error;
        }

        if (!(await network.confirmOfflineAfterFailure())) {
          throw error;
        }
      }
    }
  }

  private reportRetryExhaustion(lang: string, error: unknown): void {
    if (this.retryExhaustionReported) {
      return;
    }

    this.retryExhaustionReported = true;
    this.options.onRetryExhausted?.({ lang, error });
  }
}

class TranslationRetryExhaustedError extends Error {
  constructor(readonly cause: unknown) {
    super('Translation retry exhausted.');
  }
}

export function normalizeLang(lang: string): string {
  const normalizedLang = lang.trim().toLowerCase().split('-')[0] ?? DEFAULT_LANG;

  return normalizedLang || DEFAULT_LANG;
}

export function isRetryableTranslationError(error: unknown): boolean {
  if (!(error instanceof HttpErrorResponse)) {
    return true;
  }

  if (error.status === 404) {
    return false;
  }

  if (error.status === 0 || error.status === 200 || error.status >= 500) {
    return true;
  }

  return error.status === 408 || error.status === 429;
}

function isOfflineTranslationError(error: unknown): boolean {
  return error instanceof HttpErrorResponse && error.status === 0;
}
